<script lang="ts">
    import type { ClassValue, SvelteHTMLElements } from 'svelte/elements'

    // Figma sources:
    // https://www.figma.com/design/Q1oochZOrHxQUlYIamPDNW/%F0%9F%8E%A8-Design-Kit?node-id=10614-217511&m=dev
    // https://www.figma.com/design/Q1oochZOrHxQUlYIamPDNW/%F0%9F%8E%A8-Design-Kit?node-id=10615-226635&m=dev
    export type SpinnerSize = 'small' | 'medium' | 'large' | 'extra-large'
    export type SpinnerBackground = 'light' | 'dark'

    type NativeSpanAttributes = Omit<
        SvelteHTMLElements['span'],
        'aria-hidden' | 'aria-label' | 'children' | 'class' | 'role' | 'style'
    >

    export type SpinnerProps = NativeSpanAttributes & {
        ariaLabel?: string
        background?: SpinnerBackground
        class?: ClassValue
        decorative?: boolean
        segmentColor?: string
        size?: SpinnerSize
        style?: string
        trackColor?: string
    }

    const SizeClass: Record<SpinnerSize, string> = {
        small: 'ts-spinner--small',
        medium: 'ts-spinner--medium',
        large: 'ts-spinner--large',
        'extra-large': 'ts-spinner--extra-large'
    }
    const BackgroundClass: Record<SpinnerBackground, string> = {
        light: 'ts-spinner--light',
        dark: 'ts-spinner--dark'
    }

    let {
        ariaLabel = 'Loading',
        background = 'light',
        class: className,
        decorative = false,
        segmentColor,
        size = 'medium',
        style,
        trackColor,
        ...spanAttributes
    }: SpinnerProps = $props()

    const ariaHidden = $derived(decorative ? true : undefined)
    const resolvedAriaLabel = $derived(decorative ? undefined : ariaLabel)
    const role = $derived(decorative ? undefined : 'status')
    const colorStyle = $derived(
        [
            segmentColor ? `--ts-spinner-segment-color: ${segmentColor};` : undefined,
            trackColor ? `--ts-spinner-track-color: ${trackColor};` : undefined
        ]
            .filter(Boolean)
            .join(' ') || undefined
    )
    const resolvedStyle = $derived([style, colorStyle].filter(Boolean).join(' ') || undefined)
</script>

<span
    {...spanAttributes}
    aria-hidden={ariaHidden}
    aria-label={resolvedAriaLabel}
    class={['ts-spinner', SizeClass[size], BackgroundClass[background], className]}
    {role}
    style={resolvedStyle}
>
    <span class="ts-spinner__segment"></span>
</span>

<style>
    .ts-spinner {
        --ts-spinner-segment-color: var(--color-ts-primary-main);
        --ts-spinner-track-color: var(--color-ts-gray-200);
        --ts-spinner-stroke-width: 2px;
        block-size: var(--ts-spinner-size);
        border: var(--ts-spinner-stroke-width) solid var(--ts-spinner-track-color);
        border-radius: 9999px;
        box-sizing: border-box;
        display: inline-block;
        inline-size: var(--ts-spinner-size);
        position: relative;
        vertical-align: middle;
    }

    .ts-spinner--dark {
        --ts-spinner-track-color: var(--color-ts-gray-000);
    }

    .ts-spinner--small {
        --ts-spinner-size: 18px;
        --ts-spinner-stroke-width: 1.5px;
    }

    .ts-spinner--medium {
        --ts-spinner-size: 24px;
        --ts-spinner-stroke-width: 2px;
    }

    .ts-spinner--large {
        --ts-spinner-size: 32px;
        --ts-spinner-stroke-width: 3px;
    }

    .ts-spinner--extra-large {
        --ts-spinner-size: 64px;
        --ts-spinner-stroke-width: 6px;
    }

    .ts-spinner__segment {
        animation: ts-spinner-rotate 900ms linear infinite;
        border: var(--ts-spinner-stroke-width) solid transparent;
        border-radius: inherit;
        border-top-color: var(--ts-spinner-segment-color);
        box-sizing: border-box;
        inset: calc(-1 * var(--ts-spinner-stroke-width));
        position: absolute;
    }

    @keyframes ts-spinner-rotate {
        from {
            transform: rotate(45deg);
        }

        to {
            transform: rotate(405deg);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .ts-spinner__segment {
            animation-duration: 1800ms;
        }
    }
</style>
